import {
  SupportCase,
  SupportQueue,
  SupportQueueId,
  FinancialCalculationEscalation,
  BillingSupportDispute,
  DeveloperSupportTicket,
  KnowledgeArticle,
  KnownIssue,
  KnowledgeGap,
  SupportMacro,
  SupportQAReview,
  VoiceOfCustomerTheme,
  CSATResponse,
  NPSMetricData
} from './supportTypes';

export const MOCK_SUPPORT_QUEUES: SupportQueue[] = [
  {
    id: 'general-support',
    name: 'General & Product Inquiries',
    domain: 'GENERAL',
    description: 'Triage, navigational guidance, onboarding, feature requests and general feedback.',
    assignedAgents: 8,
    openCases: 14,
    slaTargetMinutes: 240, // 4 hours
    tier: 'L1_GENERAL',
    isHighRisk: false,
  },
  {
    id: 'account-login',
    name: 'Account, Identity & Authentication',
    domain: 'AUTHENTICATION',
    description: 'Passkey setup, MFA recovery, account lockout, device verification (Strict zero-bypass policy).',
    assignedAgents: 6,
    openCases: 9,
    slaTargetMinutes: 60, // 1 hour
    tier: 'L2_SPECIALIST',
    isHighRisk: true,
  },
  {
    id: 'financial-calculations',
    name: 'Financial Calculations & Tax Escalation Desk',
    domain: 'CALCULATORS',
    description: 'Salary/Payroll tax rulesets, mortgage amortizations, currency conversion math disputes.',
    assignedAgents: 5,
    openCases: 7,
    slaTargetMinutes: 120, // 2 hours
    tier: 'L2_SPECIALIST',
    isHighRisk: true,
  },
  {
    id: 'billing-payments',
    name: 'Billing, Subscriptions & Monetization',
    domain: 'BILLING',
    description: 'Prompt 21 invoice disputes, failed renewals, duplicate charge remediation, refund requests.',
    assignedAgents: 4,
    openCases: 6,
    slaTargetMinutes: 60, // 1 hour
    tier: 'L2_SPECIALIST',
    isHighRisk: true,
  },
  {
    id: 'developer-support',
    name: 'Developer API & Integration Desk',
    domain: 'DEVELOPER_API',
    description: 'Prompt 20 SDK integration bugs, Request ID diagnostics, rate limit increases, sandbox testing.',
    assignedAgents: 4,
    openCases: 5,
    slaTargetMinutes: 120, // 2 hours
    tier: 'L2_SPECIALIST',
    isHighRisk: false,
  },
  {
    id: 'privacy-rights',
    name: 'Data Privacy & DSAR Routing',
    domain: 'PRIVACY',
    description: 'GDPR/CCPA access, rectification, portability requests routed directly to Legal/DPO.',
    assignedAgents: 2,
    openCases: 2,
    slaTargetMinutes: 720, // 12 hours (statutory 30d legal clock)
    tier: 'L3_ENGINEERING_LEGAL',
    isHighRisk: true,
  },
  {
    id: 'accessibility-support',
    name: 'Accessibility (A11y) & WCAG Feedback',
    domain: 'ACCESSIBILITY',
    description: 'Screen reader obstacles, keyboard navigation failures, high contrast requests.',
    assignedAgents: 2,
    openCases: 3,
    slaTargetMinutes: 240,
    tier: 'L2_SPECIALIST',
    isHighRisk: false,
  },
  {
    id: 'compliance-escalations',
    name: 'Regulatory & Formal Complaints Desk',
    domain: 'COMPLIANCE',
    description: 'Formal consumer grievances, regulatory ombudsman notifications, AML/Sanctions friction.',
    assignedAgents: 2,
    openCases: 1,
    slaTargetMinutes: 60,
    tier: 'L3_ENGINEERING_LEGAL',
    isHighRisk: true,
  },
  {
    id: 'security-routing',
    name: 'Security Incident & Vulnerability Triage',
    domain: 'SECURITY',
    description: 'Responsible disclosures, phishing reports, session takeover flags (Direct Prompt 17 hook).',
    assignedAgents: 3,
    openCases: 1,
    slaTargetMinutes: 15, // 15 mins critical
    tier: 'L3_ENGINEERING_LEGAL',
    isHighRisk: true,
  }
];

export const MOCK_SUPPORT_CASES: SupportCase[] = [
  {
    id: 'case_f82901a',
    publicId: 'YP-CASE-84920',
    customerUserId: 'usr_sarah_jenkins_77',
    customerName: 'Sarah Jenkins',
    customerEmail: 's.jenkins@techfin.co.uk',
    customerPlan: 'PRO_MONTHLY',
    market: 'GB',
    locale: 'en-GB',
    language: 'en',
    channel: 'IN_APP',
    domain: 'CALCULATORS',
    category: 'Salary & Payroll Tax',
    subcategory: 'Student Loan Plan 2 Deduction',
    subject: 'Scottish Tax Band & Student Loan Plan 2 discrepancy in UK Take-Home Pay',
    priority: 'P1_HIGH',
    status: 'ESCALATED',
    assignedQueue: 'financial-calculations',
    assignedAgent: 'David Sterling (Financial Specialist)',
    createdAt: '2026-08-25T01:30:00Z',
    firstResponseAt: '2026-08-25T01:45:00Z',
    firstResponseDueAt: '2026-08-25T02:30:00Z',
    resolutionDueAt: '2026-08-25T05:30:00Z',
    slaStatus: 'ON_TRACK',
    financialEscalationId: 'FCE-9382',
    tags: ['UK-Payroll', 'HMRC-2026/27', 'Student-Loan', 'Scottish-Rates'],
    messages: [
      {
        id: 'msg_001',
        caseId: 'case_f82901a',
        senderType: 'CUSTOMER',
        senderName: 'Sarah Jenkins',
        content: `Hi YamooPay team, I live in Edinburgh (Scottish tax resident) and earn £62,000 gross. In the UK Salary Calculator, when I check 'Scottish Tax' and 'Student Loan Plan 2', the monthly net take-home pay is shown as £3,412.18, whereas my actual HMRC payslip shows £3,387.40. Is the 9% threshold for Plan 2 (£27,295) or £28,470 for the 2026/27 tax year in your engine?`,
        timestamp: '2026-08-25T01:30:00Z'
      },
      {
        id: 'msg_002',
        caseId: 'case_f82901a',
        senderType: 'AGENT',
        senderName: 'David Sterling (Financial Specialist)',
        content: `Hello Sarah, thank you for reaching out with exact calculation figures. We take any potential payroll calculation discrepancy extremely seriously. I have replicated your scenario and immediately escalated this to our Financial Validation Team under reference FCE-9382 to verify the 2026/27 Scottish Starter/Basic rate bands against HMRC statutory documentation. We will provide a formal audit outcome within 2 hours.`,
        timestamp: '2026-08-25T01:45:00Z'
      },
      {
        id: 'msg_003',
        caseId: 'case_f82901a',
        senderType: 'INTERNAL_NOTE',
        senderName: 'David Sterling',
        content: `NOTE FOR QA / ENG: Re-verified HMRC Scottish Bandings 2026/27. The Scottish Intermediate rate threshold is currently configured in ruleset UK_HMRC_2026_V1 at £26,561 instead of the updated £26,800. Escalated to Prompt 23 CQO desk.`,
        timestamp: '2026-08-25T01:50:00Z'
      }
    ]
  },
  {
    id: 'case_b10928c',
    publicId: 'YP-CASE-84921',
    customerUserId: 'usr_marc_dupont_42',
    customerName: 'Marc Dupont',
    customerEmail: 'm.dupont@entreprise-lyon.fr',
    customerPlan: 'PRO_ANNUAL',
    market: 'FR',
    locale: 'fr-FR',
    language: 'fr',
    channel: 'BILLING_PORTAL',
    domain: 'BILLING',
    category: 'Invoicing & Charges',
    subcategory: 'VAT Reverse Charge on Annual Subscription',
    subject: 'Facture avec TVA française au lieu de l’autoliquidation intra-communautaire FR',
    priority: 'P1_HIGH',
    status: 'OPEN',
    assignedQueue: 'billing-payments',
    assignedAgent: 'Claire Fontaine (Billing Ops)',
    createdAt: '2026-08-25T02:15:00Z',
    firstResponseAt: '2026-08-25T02:40:00Z',
    firstResponseDueAt: '2026-08-25T03:15:00Z',
    resolutionDueAt: '2026-08-25T06:15:00Z',
    slaStatus: 'ON_TRACK',
    billingTransactionRef: 'txn_yp_stripe_91823901',
    tags: ['VAT-EU', 'B2B-Invoice', 'Stripe-Billing', 'France'],
    messages: [
      {
        id: 'msg_101',
        caseId: 'case_b10928c',
        senderType: 'CUSTOMER',
        senderName: 'Marc Dupont',
        content: `Bonjour, nous avons souscrit à l'abonnement YamooPay Pro Entreprise annuel (348 € TTC). Notre numéro de TVA intracommunautaire (FR82819203940) est valide sur le système VIES, mais la facture générée mentionne 20% de TVA. Pouvez-vous régulariser et émettre un avoir / facture HT ?`,
        originalLanguage: 'fr',
        timestamp: '2026-08-25T02:15:00Z'
      },
      {
        id: 'msg_102',
        caseId: 'case_b10928c',
        senderType: 'AGENT',
        senderName: 'Claire Fontaine (Billing Ops)',
        content: `Bonjour Monsieur Dupont, nous avons vérifié votre numéro de TVA intracommunautaire sur le registre VIES. Votre profil a été mis à jour avec le statut B2B Exonéré (Autoliquidation art. 283-1 du CGI). Une note de crédit de 58,00 € a été transmise à notre passerelle de paiement Stripe, et votre facture révisée HT est disponible dans votre espace client.`,
        originalLanguage: 'fr',
        timestamp: '2026-08-25T02:40:00Z'
      }
    ]
  },
  {
    id: 'case_d77182e',
    publicId: 'YP-CASE-84922',
    customerUserId: 'usr_dev_alex_chen',
    customerName: 'Alex Chen',
    customerEmail: 'alex@fintechscale.io',
    customerPlan: 'ENTERPRISE_API',
    market: 'US',
    locale: 'en-US',
    language: 'en',
    channel: 'DEVELOPER_PORTAL',
    domain: 'DEVELOPER_API',
    category: 'API Integration',
    subcategory: 'Rate Limit 429 on High-Volume FX Endpoint',
    subject: 'Request ID: req_yp_live_4490182 429 Too Many Requests in US-East-1',
    priority: 'P1_HIGH',
    status: 'TRIAGED',
    assignedQueue: 'developer-support',
    assignedAgent: 'Liam Vance (Senior Dev Support)',
    createdAt: '2026-08-25T02:45:00Z',
    firstResponseDueAt: '2026-08-25T03:45:00Z',
    resolutionDueAt: '2026-08-25T06:45:00Z',
    slaStatus: 'ON_TRACK',
    developerRequestId: 'req_yp_live_4490182',
    tags: ['Developer-API', 'FX-Rates', 'Rate-Limit', 'Enterprise-SLA'],
    messages: [
      {
        id: 'msg_201',
        caseId: 'case_d77182e',
        senderType: 'CUSTOMER',
        senderName: 'Alex Chen',
        content: `We are running batch FX conversion estimates across 50 currency pairs every 30 seconds for our treasury portal. We are hitting HTTP 429 intermittently. Request ID: req_yp_live_4490182. Our contracted Tier-2 quota is 100 req/sec, but headers indicate we are throttled at 20 req/sec. Please check IP gateway routing.`,
        timestamp: '2026-08-25T02:45:00Z'
      }
    ]
  },
  {
    id: 'case_p99201f',
    publicId: 'YP-CASE-84923',
    customerUserId: 'usr_elena_rostova',
    customerName: 'Elena Rostova',
    customerEmail: 'e.rostova@berlin-tech.de',
    customerPlan: 'FREE',
    market: 'DE',
    locale: 'de-DE',
    language: 'de',
    channel: 'WEB_FORM',
    domain: 'PRIVACY',
    category: 'GDPR Data Subject Rights',
    subcategory: 'Article 17 Right to Erasure (Account & Analytics Data)',
    subject: 'Antrag auf Datenlöschung gem. Art. 17 DSGVO (Konto & Berechnungsverlauf)',
    priority: 'P2_NORMAL',
    status: 'OPEN',
    assignedQueue: 'privacy-rights',
    assignedAgent: 'Sophie Weber (Privacy Coordinator)',
    createdAt: '2026-08-25T03:00:00Z',
    firstResponseDueAt: '2026-08-25T15:00:00Z',
    resolutionDueAt: '2026-09-24T03:00:00Z', // 30-day GDPR statutory clock
    slaStatus: 'ON_TRACK',
    privacyRequestId: 'DSR-2026-089',
    tags: ['GDPR', 'Art-17-Erasure', 'Germany', 'Legal-Routing'],
    messages: [
      {
        id: 'msg_301',
        caseId: 'case_p99201f',
        senderType: 'CUSTOMER',
        senderName: 'Elena Rostova',
        content: `Sehr geehrte Damen und Herren, hiermit mache ich von meinem Recht auf Löschung meiner personenbezogenen Daten gemäß Art. 17 DSGVO Gebrauch. Bitte löschen Sie mein YamooPay Konto sowie alle gespeicherten Simulationsdaten.`,
        originalLanguage: 'de',
        timestamp: '2026-08-25T03:00:00Z'
      }
    ]
  },
  {
    id: 'case_a11y_001',
    publicId: 'YP-CASE-84924',
    customerUserId: 'usr_jordan_reed',
    customerName: 'Jordan Reed',
    customerEmail: 'jordan.accessibility@nvda-users.org',
    customerPlan: 'FREE',
    market: 'US',
    locale: 'en-US',
    language: 'en',
    channel: 'IN_APP',
    domain: 'ACCESSIBILITY',
    category: 'Screen Reader Interaction',
    subcategory: 'Mortgage Amortization Schedule Focus Trap',
    subject: 'Keyboard focus trap in Mortgage Amortization Table when navigating with NVDA',
    priority: 'P2_NORMAL',
    status: 'OPEN',
    assignedQueue: 'accessibility-support',
    assignedAgent: 'Marcus Bell (A11y Specialist)',
    createdAt: '2026-08-25T03:10:00Z',
    firstResponseDueAt: '2026-08-25T07:10:00Z',
    resolutionDueAt: '2026-08-26T03:10:00Z',
    slaStatus: 'ON_TRACK',
    tags: ['WCAG-2.2', 'NVDA', 'Keyboard-Trap', 'Mortgage-Table'],
    messages: [
      {
        id: 'msg_401',
        caseId: 'case_a11y_001',
        senderType: 'CUSTOMER',
        senderName: 'Jordan Reed',
        content: `Hello, when using NVDA screen reader on the Mortgage Calculator Amortization Schedule table, tabbing into the 'Extra Monthly Principal' input field prevents Shift+Tab from navigating back to the summary cards. Please verify aria-live region and tabindex logic.`,
        timestamp: '2026-08-25T03:10:00Z'
      }
    ]
  }
];

export const MOCK_FINANCIAL_ESCALATIONS: FinancialCalculationEscalation[] = [
  {
    id: 'FCE-9382',
    caseId: 'case_f82901a',
    tool: 'SALARY_TAX',
    market: 'GB',
    jurisdiction: 'SCOTLAND_HMRC',
    rulesetVersion: 'UK_HMRC_2026_V1.2',
    formulaVersion: 'UK_PAYE_ENGINE_2026.4',
    inputSnapshot: {
      grossAnnualSalary: 62000,
      taxCode: 'S1257L',
      isScottishTaxResident: true,
      studentLoanPlan: 'PLAN_2',
      pensionReliefType: 'SALARY_SACRIFICE',
      pensionEmployeePct: 5
    },
    systemOutputSnapshot: {
      netMonthlyTakeHome: 3412.18,
      scottishIncomeTaxMonthly: 981.42,
      nationalInsuranceMonthly: 284.10,
      studentLoanMonthly: 218.30
    },
    customerClaimedDiscrepancy: 'Customer stated monthly take home is £3,387.40 vs our £3,412.18. Suspects Scottish Intermediate threshold band misalignment.',
    customerExpectedOutput: '£3,387.40 net take home',
    status: 'RULESET_REVIEW',
    investigatorNotes: 'Financial Validation Lab verified HMRC statutory schedule. Scottish Intermediate rate threshold is £26,800 in 2026/27. Engine was on £26,561 draft figures.',
    validatorName: 'Dr. Arthur Pendelton (Chief Tax Actuary)',
    verifiedAt: '2026-08-25T02:00:00Z',
    linkedQualityDefectId: 'QD-2026-TAX-044'
  },
  {
    id: 'FCE-9383',
    caseId: 'case_calc_us_mortgage',
    tool: 'MORTGAGE',
    market: 'US',
    jurisdiction: 'CALIFORNIA_DRE',
    rulesetVersion: 'US_CFPB_TRID_2026_V3',
    formulaVersion: 'MORTGAGE_AMORT_V4.1',
    inputSnapshot: {
      homePrice: 850000,
      downPaymentPct: 20,
      loanTermYears: 30,
      interestRatePct: 6.75,
      propertyTaxRatePct: 1.25,
      homeInsuranceAnnual: 1800,
      pmiRequired: false
    },
    systemOutputSnapshot: {
      monthlyPrincipalAndInterest: 4410.68,
      monthlyPropertyTax: 885.42,
      monthlyHomeInsurance: 150.00,
      totalMonthlyPITI: 5446.10
    },
    customerClaimedDiscrepancy: 'Customer asked why APR differs from Note Rate (6.75% vs 6.89% APR).',
    status: 'CONFIRMED_CORRECT',
    investigatorNotes: 'Explanation provided regarding CFPB TRID origination points and prepaid finance charges incorporated into APR calculations. No bug detected.',
    validatorName: 'Elena Rostova (Senior Actuary)',
    verifiedAt: '2026-08-24T18:00:00Z'
  }
];

export const MOCK_KNOWLEDGE_ARTICLES: KnowledgeArticle[] = [
  {
    id: 'art_001',
    slug: 'understanding-scottish-income-tax-vs-uk-mainland',
    title: 'Understanding Scottish Income Tax vs UK Mainland Rates (2026/27)',
    summary: 'Detailed explanation of Starter, Basic, Intermediate, Higher, Advanced and Top rates for Scottish tax residents.',
    domain: 'TAX',
    category: 'Salary & Deductions',
    audience: 'PUBLIC_ALL',
    market: 'GB',
    locale: 'en-GB',
    status: 'PUBLISHED',
    riskLevel: 'FINANCIAL',
    contentMarkdown: `### Scottish Tax Residency (HMRC)

If your primary residence is in Scotland, your income is taxed under Scottish Income Tax rates set by the Scottish Parliament, administered by HMRC using an 'S' prefix tax code (e.g. \`S1257L\`).

#### 2026/27 Scottish Income Tax Bands:
- **Starter Rate (19%)**: £12,571 to £14,876
- **Basic Rate (20%)**: £14,877 to £26,800
- **Intermediate Rate (21%)**: £26,801 to £43,662
- **Higher Rate (42%)**: £43,663 to £75,000
- **Advanced Rate (45%)**: £75,001 to £125,140
- **Top Rate (48%)**: Over £125,140

National Insurance contributions (NIC) remain UK-wide and are not devolved.`,
    productVersion: 'v2026.8.0',
    rulesetReference: 'UK_HMRC_2026_V1.2',
    author: 'David Sterling',
    reviewedBy: 'Dr. Arthur Pendelton (Chief Tax Actuary)',
    lastReviewedAt: '2026-08-20T10:00:00Z',
    nextReviewAt: '2026-11-20T10:00:00Z',
    helpfulCount: 428,
    notHelpfulCount: 12,
    viewsCount: 18450,
    tags: ['UK', 'Scotland', 'HMRC', 'Income-Tax', 'PAYE']
  },
  {
    id: 'art_002',
    slug: 'what-is-the-difference-between-interest-rate-and-apr',
    title: 'What is the Difference Between Nominal Interest Rate and APR?',
    summary: 'Clear breakdown of borrowing rate vs Annual Percentage Rate including mandatory lender fees.',
    domain: 'MORTGAGE',
    category: 'Loans & Mortgages',
    audience: 'PUBLIC_ALL',
    market: 'GLOBAL',
    locale: 'en-US',
    status: 'PUBLISHED',
    riskLevel: 'FINANCIAL',
    contentMarkdown: `### Nominal Interest Rate vs APR

When shopping for a mortgage or personal loan, you will see two numbers:
1. **Nominal Interest Rate (Note Rate)**: The base cost of borrowing the principal balance per annum.
2. **APR (Annual Percentage Rate)**: The true, all-in annualized cost of credit reflecting the interest rate **plus** lender origination fees, closing costs, points, mortgage broker fees, and mandatory upfront charges.

YamooPay calculators display both Note Rate amortization and statutory APR under Truth in Lending Act (TILA) / Consumer Credit Directive requirements.`,
    productVersion: 'v2026.8.0',
    rulesetReference: 'US_CFPB_TILA_2026',
    author: 'Marcus Vance',
    reviewedBy: 'Sarah Al-Mansoor (Legal Counsel)',
    lastReviewedAt: '2026-08-15T14:00:00Z',
    nextReviewAt: '2026-11-15T14:00:00Z',
    helpfulCount: 914,
    notHelpfulCount: 8,
    viewsCount: 34200,
    tags: ['APR', 'Mortgage', 'TILA', 'Interest-Rate', 'CFPB']
  },
  {
    id: 'art_003',
    slug: 'developer-api-rate-limits-and-best-practices',
    title: 'Developer API: Understanding Rate Limits, HTTP 429 & Retry-After',
    summary: 'Guidance on exponential backoff, header inspection and quota upgrades for YamooPay API developers.',
    domain: 'DEVELOPER_API',
    category: 'API & Webhooks',
    audience: 'DEVELOPERS',
    market: 'GLOBAL',
    locale: 'en-US',
    status: 'PUBLISHED',
    riskLevel: 'DEVELOPER',
    contentMarkdown: `### Rate Limit Headers

Every API response from \`api.yamoopay.com\` contains canonical RFC 6585 rate limiting headers:
- \`X-RateLimit-Limit\`: Maximum permitted requests in current window.
- \`X-RateLimit-Remaining\`: Number of remaining requests before throttle.
- \`X-RateLimit-Reset\`: Epoch timestamp (in seconds) when the quota resets.

#### Handling HTTP 429 Responses:
When receiving a \`429 Too Many Requests\`, inspect the \`Retry-After\` header and apply exponential backoff with jitter. For enterprise batch workloads, request a quota increase through the Developer Command Center.`,
    productVersion: 'v2026.8.0',
    author: 'Liam Vance',
    reviewedBy: 'Alex Chen (Principal API Architect)',
    lastReviewedAt: '2026-08-18T09:00:00Z',
    nextReviewAt: '2026-11-18T09:00:00Z',
    helpfulCount: 312,
    notHelpfulCount: 4,
    viewsCount: 9800,
    tags: ['API', 'HTTP-429', 'Rate-Limit', 'Developer-SDK']
  },
  {
    id: 'art_004',
    slug: 'internal-runbook-handling-statutory-dsar-requests',
    title: '[INTERNAL RUNBOOK] Handling GDPR/CCPA Data Subject Rights (DSAR) & Erasure',
    summary: 'Restricted agent runbook for identity step-up and Prompt 22 privacy routing.',
    domain: 'PRIVACY',
    category: 'Internal Support Runbooks',
    audience: 'AGENT_INTERNAL_ONLY',
    market: 'GLOBAL',
    locale: 'en-US',
    status: 'PUBLISHED',
    riskLevel: 'PRIVACY',
    contentMarkdown: `### RESTRICTED: Support Agent Operational Runbook for DSAR Requests

1. **Intake & Verification**:
   - Verify if customer is authenticated via passkey/MFA.
   - For unauthenticated web form requests, initiate email magic-link step-up verification.
   - **NEVER** ask for photo ID or passport scans in standard support ticketing.

2. **Routing to Privacy Platform**:
   - Create a linked record in Prompt 22 Privacy Rights Platform.
   - Do **NOT** manually delete customer records from database or CRM.
   - The statutory 30-day compliance clock is managed by Legal/DPO.

3. **Customer Communication**:
   - Use approved macro \`MACRO_PRIVACY_DSAR_ACK_V2\`. No improvised legal statements.`,
    productVersion: 'v2026.8.0',
    author: 'Sophie Weber',
    reviewedBy: 'Sarah Al-Mansoor (Chief Compliance Officer)',
    lastReviewedAt: '2026-08-10T11:00:00Z',
    nextReviewAt: '2026-11-10T11:00:00Z',
    helpfulCount: 45,
    notHelpfulCount: 0,
    viewsCount: 450,
    tags: ['Internal', 'DSAR', 'GDPR', 'Runbook']
  }
];

export const MOCK_KNOWN_ISSUES: KnownIssue[] = [
  {
    id: 'ki_001',
    publicCode: 'YP-ISSUE-2026-08-TAX',
    title: 'Scottish 2026/27 Intermediate Tax Band Recalibration',
    domain: 'TAX',
    affectedProducts: ['UK Salary & Paycheck Calculator', 'UK Contractor Tax Estimator'],
    affectedMarkets: ['GB (Scotland)'],
    affectedVersions: ['v2026.8.0 - v2026.8.2'],
    status: 'IDENTIFIED',
    severity: 'MAJOR',
    symptomsPublic: 'Scottish tax residents earning between £26,561 and £43,662 may observe an approximate £2.10/month difference in estimated take-home pay.',
    safeWorkaround: 'Our engine is updating to HMRC Scottish Schedule v1.2 today at 14:00 UTC. No manual action required.',
    publishedAt: '2026-08-25T02:00:00Z',
    incidentRef: 'INC-2026-08-41'
  },
  {
    id: 'ki_002',
    publicCode: 'YP-ISSUE-2026-08-ADYEN',
    title: 'Intermittent 3D-Secure 2.2 Timeout on Euro Direct Debit Initial Setup',
    domain: 'PAYMENT',
    affectedProducts: ['Pro Subscription Billing', 'Enterprise Developer Portal'],
    affectedMarkets: ['FR', 'DE', 'NL'],
    affectedVersions: ['Prompt 21 Billing Gateway'],
    status: 'MONITORING',
    severity: 'MINOR',
    symptomsPublic: 'Occasional delay in bank verification popups for SEPA Direct Debit mandating.',
    safeWorkaround: 'Credit/Debit card and PayPal payments are fully operational.',
    publishedAt: '2026-08-24T22:30:00Z',
    resolvedAt: '2026-08-25T01:15:00Z',
    incidentRef: 'INC-2026-08-39'
  }
];

export const MOCK_KNOWLEDGE_GAPS: KnowledgeGap[] = [
  {
    id: 'gap_001',
    searchQueryCluster: 'French PFU Flat Tax 30% vs Progressive Barème Dividends',
    searchVolume30d: 1420,
    zeroResultCount: 840,
    linkedTicketVolume: 28,
    domain: 'TAX',
    priority: 'P0_CRITICAL',
    suggestedAction: 'Create dedicated French Investment Income Tax guide covering 12.8% IR + 17.2% Social Contributions.',
    status: 'IN_REVIEW'
  },
  {
    id: 'gap_002',
    searchQueryCluster: 'US 1031 Exchange Real Estate Capital Gains Rollover in Wealth Planner',
    searchVolume30d: 910,
    zeroResultCount: 620,
    linkedTicketVolume: 19,
    domain: 'WEALTH',
    priority: 'P1_HIGH',
    suggestedAction: 'Author article clarifying YamooPay Wealth simulation assumptions for IRS Section 1031 like-kind exchanges.',
    status: 'DRAFTING'
  },
  {
    id: 'gap_003',
    searchQueryCluster: 'Webhook Signature verification with Ed25519 in Go SDK',
    searchVolume30d: 380,
    zeroResultCount: 290,
    linkedTicketVolume: 12,
    domain: 'DEVELOPER_API',
    priority: 'P1_HIGH',
    suggestedAction: 'Add complete Go code snippet to Developer API Knowledge Base.',
    status: 'IDENTIFIED'
  }
];

export const MOCK_SUPPORT_MACROS: SupportMacro[] = [
  {
    id: 'macro_fin_01',
    title: 'Financial Calculation Escalation Acknowledgment (Prompt 23 Link)',
    domain: 'CALCULATORS',
    triggerCategory: 'Calculation Discrepancy',
    riskLevel: 'FINANCIAL',
    templateBody: `Hello {{customerName}},

Thank you for contacting YamooPay regarding your {{calculatorName}} calculation. We take mathematical precision and statutory accuracy with the highest level of rigor.

Your scenario has been logged under Financial Validation Reference {{escalationId}} and assigned to our Actuarial & Financial Engineering Desk. We are actively reproducing the simulation against our verified {{rulesetVersion}} statutory benchmarks.

We will provide a complete, verified explanation of the formulas and bandings applied within {{slaHours}} hours.

Sincerely,
YamooPay Financial Experience Team`,
    variables: ['customerName', 'calculatorName', 'escalationId', 'rulesetVersion', 'slaHours'],
    requiresManagerReview: false,
    version: '2.1'
  },
  {
    id: 'macro_bill_02',
    title: 'B2B VAT Reverse Charge & Invoicing Adjustment (Prompt 21 Link)',
    domain: 'BILLING',
    triggerCategory: 'VAT / Invoice Correction',
    riskLevel: 'BILLING',
    templateBody: `Bonjour {{customerName}},

Nous avons bien pris en compte votre demande concernant la TVA intracommunautaire pour votre entreprise {{companyName}}.

Après vérification de votre numéro de TVA {{vatNumber}} auprès du service VIES de la Commission Européenne, votre compte a été actualisé en régime d'autoliquidation (B2B Hors Taxe).

La facture rectificative n° {{invoiceNumber}} ainsi que la note de crédit associée sont dès à présent téléchargeables dans votre Espace Client YamooPay.

Bien cordialement,
L'équipe Facturation YamooPay`,
    variables: ['customerName', 'companyName', 'vatNumber', 'invoiceNumber'],
    requiresManagerReview: true,
    version: '1.4'
  }
];

export const MOCK_VOC_THEMES: VoiceOfCustomerTheme[] = [
  {
    id: 'voc_001',
    themeName: 'UK Scottish vs English Tax Clarity in Paycheck UI',
    domain: 'CALCULATORS',
    sentiment: 'NEUTRAL',
    ticketVolume30d: 142,
    trendPercentage: 18.5,
    impactScore: 88,
    representativeQuotes: [
      "The toggle for Scottish tax is clear, but I wasn't sure if Student Loan Plan 2 reflects 2026/27 uplift.",
      "Would love a side-by-side comparison of take-home pay if moving from London to Glasgow."
    ],
    productActionItem: 'Add visual statutory band preview card under Scottish tax checkbox in UK Salary Tool.',
    status: 'PLANNED_IN_ROADMAP'
  },
  {
    id: 'voc_002',
    themeName: 'Developer Portal Webhook Event Log Real-Time Filtering',
    domain: 'DEVELOPER_API',
    sentiment: 'POSITIVE',
    ticketVolume30d: 64,
    trendPercentage: -12.0,
    impactScore: 74,
    representativeQuotes: [
      "The Request ID lookup is super fast. Can we have webhook delivery payload resend directly from UI?",
      "Go SDK examples are great, need Python asyncio examples for high-throughput."
    ],
    productActionItem: 'Release Webhook Retry Simulation button in Developer Portal P20.',
    status: 'SHIPPED_FIX'
  },
  {
    id: 'voc_003',
    themeName: 'High Contrast Mode for Retirement FIRE Projections',
    domain: 'ACCESSIBILITY',
    sentiment: 'MIXED',
    ticketVolume30d: 31,
    trendPercentage: 5.2,
    impactScore: 68,
    representativeQuotes: [
      "The green and emerald curves for safe withdrawal rate blend together on low-contrast screens.",
      "Please ensure chart legends have distinct dashed line patterns for colorblind users."
    ],
    productActionItem: 'Implement distinct stroke-dasharray and pattern fills for all D3/Recharts curves.',
    status: 'UNDER_PM_REVIEW'
  }
];

export const MOCK_CSAT_RESPONSES: CSATResponse[] = [
  {
    id: 'csat_01',
    casePublicId: 'YP-CASE-84915',
    customerUserId: 'usr_david_m',
    score: 5,
    category: 'CALCULATORS',
    market: 'US',
    comment: 'The explanation of the mortgage amortization points was crystal clear and very fast!',
    createdAt: '2026-08-24T19:30:00Z'
  },
  {
    id: 'csat_02',
    casePublicId: 'YP-CASE-84912',
    customerUserId: 'usr_claire_b',
    score: 5,
    category: 'BILLING',
    market: 'FR',
    comment: 'Facture B2B corrigée en moins de 30 minutes. Bravo pour le professionnalisme.',
    createdAt: '2026-08-24T20:15:00Z'
  },
  {
    id: 'csat_03',
    casePublicId: 'YP-CASE-84908',
    customerUserId: 'usr_dev_klaus',
    score: 4,
    category: 'DEVELOPER_API',
    market: 'DE',
    comment: 'Fast API support, developer engineer knew the RFC specs immediately.',
    createdAt: '2026-08-24T21:00:00Z'
  }
];

export const MOCK_NPS_DATA: NPSMetricData = {
  score: 68, // Excellent fintech benchmark (+68)
  promotersPct: 76,
  passivesPct: 16,
  detractorsPct: 8,
  totalResponses: 2480,
  byMarket: {
    US: 71,
    GB: 69,
    FR: 66,
    DE: 68,
    CA: 67,
    GLOBAL: 68
  }
};
